import type { Node } from '@xyflow/react';
import type { ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk-api';
import type { NodePosition } from '../../app/api/generated/client';
import { connectorNodeSize, partnerNodeSize } from '../config/constants';
import type { ConnectorNodeData, FamilyTreeEdgeType, FamilyTreeNode, HelperLineNodeData, PartnerNodeData } from '../interfaces';
import { NodeHandlerId } from '../types/node-handler-id';
import { getEdgeHandles, isHorizontalEdge, isNodeIdInEdge } from './edge-utils';

export const isPartnerNode = (node?: FamilyTreeNode): node is Node<PartnerNodeData> => !!node && !node.data.isConnector;

export const isConnectorNode = (node?: FamilyTreeNode): node is Node<ConnectorNodeData> => !!node && node.data.isConnector;

export const isHelperLineNode = (node?: FamilyTreeNode): node is Node<HelperLineNodeData> => !!node && !!node.data.axis;

export const isVisibleNode = (node: FamilyTreeNode) =>
    isHelperLineNode(node) || ((isPartnerNode(node) || isConnectorNode(node)) && node.data.visibility === 'VISIBLE');

export const isBottomNodeHandle = (handle: NodeHandlerId) => [NodeHandlerId.SOURCE_BOTTOM, NodeHandlerId.TARGET_BOTTOM].includes(handle);

export const isNodeWithChildren = (node: FamilyTreeNode) => node.data.usedHandles.some(isBottomNodeHandle);

export const isAnyChildNodeVisible = (node: FamilyTreeNode, edges: FamilyTreeEdgeType[], nodes: FamilyTreeNode[]) => {
    const connectedChildEdges = edges.filter((edge) => isNodeIdInEdge(node.id)(edge) && edge.data?.relationshipType === 'IS_PARENT_OF');
    const childNodes = nodes.filter((n) => n.id !== node.id && connectedChildEdges.some((edge) => isNodeIdInEdge(n.id)(edge))) as Node<
        PartnerNodeData | ConnectorNodeData
    >[];

    return childNodes.some((n) => n.data.visibility === 'VISIBLE');
};

interface IRelatedNodesResult {
    relatedVerticalNodes: FamilyTreeNode[];
    relatedHorizontalNodes: FamilyTreeNode[];
}

export const hasHandleOnSide = (side: 'top' | 'bottom' | 'left' | 'right', handles: NodeHandlerId[]) => {
    if (side === 'top') return handles.some((handle) => [NodeHandlerId.SOURCE_TOP, NodeHandlerId.TARGET_TOP].includes(handle));
    if (side === 'bottom') return handles.some((handle) => [NodeHandlerId.SOURCE_BOTTOM, NodeHandlerId.TARGET_BOTTOM].includes(handle));
    if (side === 'left') return handles.some((handle) => [NodeHandlerId.SOURCE_LEFT, NodeHandlerId.TARGET_LEFT].includes(handle));
    if (side === 'right') return handles.some((handle) => [NodeHandlerId.SOURCE_RIGHT, NodeHandlerId.TARGET_RIGHT].includes(handle));

    return false;
};

export const getOppositeNodeHandle = (handle: NodeHandlerId) => {
    switch (handle) {
        case NodeHandlerId.SOURCE_BOTTOM:
            return NodeHandlerId.TARGET_TOP;
        case NodeHandlerId.TARGET_BOTTOM:
            return NodeHandlerId.SOURCE_TOP;
        case NodeHandlerId.SOURCE_TOP:
            return NodeHandlerId.TARGET_BOTTOM;
        case NodeHandlerId.TARGET_TOP:
            return NodeHandlerId.SOURCE_BOTTOM;
        case NodeHandlerId.SOURCE_LEFT:
            return NodeHandlerId.TARGET_RIGHT;
        case NodeHandlerId.TARGET_LEFT:
            return NodeHandlerId.SOURCE_RIGHT;
        case NodeHandlerId.SOURCE_RIGHT:
            return NodeHandlerId.TARGET_LEFT;
        case NodeHandlerId.TARGET_RIGHT:
            return NodeHandlerId.SOURCE_LEFT;
    }
};

const getRelatedNodes = (id: string, nodes: FamilyTreeNode[], relatedEdges: FamilyTreeEdgeType[]): IRelatedNodesResult =>
    nodes
        .filter((node) => node.id !== id)
        .reduce(
            (acc, node) => {
                const edge = relatedEdges.find(isNodeIdInEdge(node.id));
                if (!edge) return acc;

                if (isHorizontalEdge(edge)) {
                    if (!acc.relatedHorizontalNodes.includes(node)) acc.relatedHorizontalNodes.push(node);
                } else {
                    if (!acc.relatedVerticalNodes.includes(node)) acc.relatedVerticalNodes.push(node);
                }

                return acc;
            },
            { relatedVerticalNodes: [], relatedHorizontalNodes: [] } as IRelatedNodesResult,
        );

export const getUsedHandles = (node: FamilyTreeNode, nodes: FamilyTreeNode[], edges: FamilyTreeEdgeType[]): NodeHandlerId[] => {
    const relatedEdges = edges.filter(isNodeIdInEdge(node.id));
    const { relatedVerticalNodes, relatedHorizontalNodes } = getRelatedNodes(node.id, nodes, relatedEdges);

    const getNodeHandlerIdByPositionAndEdge = (node: FamilyTreeNode, relatedNode: FamilyTreeNode, direction: 'horizontal' | 'vertical') => {
        const isHandleSource = relatedEdges.filter(isNodeIdInEdge(node.id)).find(isNodeIdInEdge(relatedNode.id))!.source === node.id;

        if (direction === 'horizontal') {
            const isHandleLeft = node.position.x > relatedNode.position.x;

            if (isHandleLeft) return isHandleSource ? NodeHandlerId.SOURCE_LEFT : NodeHandlerId.TARGET_LEFT;
            else return isHandleSource ? NodeHandlerId.SOURCE_RIGHT : NodeHandlerId.TARGET_RIGHT;
        } else {
            const isHandleTop = node.position.y > relatedNode.position.y;

            if (isHandleTop) return isHandleSource ? NodeHandlerId.SOURCE_TOP : NodeHandlerId.TARGET_TOP;
            else return isHandleSource ? NodeHandlerId.SOURCE_BOTTOM : NodeHandlerId.TARGET_BOTTOM;
        }
    };

    return [
        ...new Set([
            ...relatedHorizontalNodes.map((n) => getNodeHandlerIdByPositionAndEdge(node, n, 'horizontal')),
            ...relatedVerticalNodes.map((n) => getNodeHandlerIdByPositionAndEdge(node, n, 'vertical')),
        ]),
    ];
};

interface IEnrichFamilyTreeNodesWithElkNodesResult {
    enrichedEdges: FamilyTreeEdgeType[];
    enrichedNodes: FamilyTreeNode[];
}

export const enrichFamilyTreeNodesWithElkNodes = (
    nodes: FamilyTreeNode[],
    elkNodes: ElkNode[],
    edges: FamilyTreeEdgeType[],
    elkEdges: ElkExtendedEdge[],
): IEnrichFamilyTreeNodesWithElkNodesResult => {
    const edgesWithElkLayout = edges.map((edge) => ({
        ...edge,
        ...elkEdges.find((e) => e.id === edge.id)?.layoutOptions,
    }));
    const nodesWithElkPosition = nodes.map((node) => {
        const { x, y } = elkNodes.find((n) => n.id === node.id)!;

        return { ...node, position: { x: x ?? 0, y: y ?? 0 } };
    });
    const enrichedNodes = nodesWithElkPosition.map((node) => ({
        ...node,
        data: { ...node.data, usedHandles: getUsedHandles(node, nodesWithElkPosition, edgesWithElkLayout) },
    }));

    return {
        enrichedEdges: edgesWithElkLayout.map((edge) => ({
            ...edge,
            ...getEdgeHandles(edge, enrichedNodes),
        })),
        enrichedNodes,
    };
};

export const applyStoredNodePositions = (originalNodes: FamilyTreeNode[], positionedNodes: FamilyTreeNode[], autoPosition?: boolean): FamilyTreeNode[] => {
    const nodesWithStoredPosition = originalNodes.filter((n) => 'nodePosition' in n.data && n.data.nodePosition != null);

    return positionedNodes.map((n) => {
        const nodeWithPosition = nodesWithStoredPosition.find((node) => node.id === n.id);
        const nodePosition = (
            nodeWithPosition == null || !('nodePosition' in nodeWithPosition.data) || autoPosition ? n.position : nodeWithPosition.data.nodePosition
        ) as NodePosition;

        return { ...n, position: nodePosition };
    });
};

export type Vec = { x: number; y: number };
export type Rect = { id: string; x: number; y: number; width: number; height: number };

export const getRectFromNode = (node: FamilyTreeNode) => ({
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.data.isConnector ? connectorNodeSize.width : partnerNodeSize.width,
    height: node.data.isConnector ? connectorNodeSize.height : partnerNodeSize.height,
});

export const getRectsFromNodes = (nodes: FamilyTreeNode[]): Rect[] => nodes.map(getRectFromNode);

export const getNodeCenter = (node: FamilyTreeNode | Rect) => {
    const rect: Rect = 'data' in node ? getRectFromNode(node) : node;

    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
};

/**
 * Map each node’s center point from its bounding box.
 * Spatial structures like Delaunay triangulation require points, not rectangles, to define proximity.
 * The most logical point to represent a rectangle is its center.
 */
export const getNodeCenters = (nodes: FamilyTreeNode[] | Rect[]): Map<string, Vec> => new Map(nodes.map((node) => [node.id, getNodeCenter(node)]));
